import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  makeStyles,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  Chip,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { useSetRecoilState } from 'recoil';
import { loadingState } from '../../recoil/atoms';
import { toast } from '../../api/toast';

const useStyles = makeStyles((theme) => ({
  root: {
    direction: 'rtl',
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

const roles = [
  { value: 'Admin', label: 'ادمین' },
  { value: 'Department', label: 'اداره' },
  { value: 'DepartmentManager', label: 'مدیر سازمان' },
  { value: 'Contractor', label: 'پیمانکار' },
  { value: 'Consultant', label: 'مشاور' },
  { value: 'Supervisor', label: 'ناظر' },
];

function EditUser({ id, setOpenEditUser }) {
  const classes = useStyles();
  const setGlobalLoading = useSetRecoilState(loadingState);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [showPwd, setShowPwd] = useState(false)
  const [form, setForm] = useState({
    id: null,
    username: '',
    email: '',
    name: '',
    selectedRoles: [],
    mobile: '',
    password: '',
    is_active: true,
    is_staff: false,
    is_superuser: false,
    is_subscribed: false,
  });

  // سازمان‌ها برای انتخابِ «مدیر سازمان»
  useEffect(() => {
    api.get('users/departments/').then((res) => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setDepartments(list);
    }).catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    // بارگذاری داده‌های کاربر از API - مستقیم با ID
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await api.get(`users/admin/users/${id}/`);
        
        if (response.data) {
          const user = response.data;
          const userRoles = [user.role].concat(user.role2 ? [user.role2] : []);
          setForm({
            id: user.id,
            username: user.username || '',
            email: user.email || '',
            name: user.manager_name || user.display_name || '',
            selectedRoles: userRoles,
            mobile: user.mobile ? String(user.mobile) : '',
            password: '',
            is_active: user.is_active !== undefined ? user.is_active : true,
            is_staff: user.is_staff || false,
            is_superuser: user.is_superuser || false,
            is_subscribed: user.is_subscribed !== undefined && user.is_subscribed !== null ? user.is_subscribed : false,
          });
          if (user.department_id) setDepartmentId(String(user.department_id));
        } else {
          toast('کاربر یافت نشد');
          setOpenEditUser(-1);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        toast('خطا در بارگذاری اطلاعات کاربر');
        setOpenEditUser(-1);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUser();
    }
  }, [id, setOpenEditUser]);

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    setGlobalLoading(true);
    try {
      const payload = {
        username: form.username,
        email: form.email,
        role: form.selectedRoles[0] || 'Contractor',
        role2: form.selectedRoles[1] || null,
        mobile: form.mobile ? String(form.mobile).replace(/^0/, '') : null,
        is_active: form.is_active,
        is_staff: form.is_staff,
        is_superuser: form.is_superuser,
      };

      if (form.selectedRoles.includes('DepartmentManager')) {
        if (!departmentId) {
          toast('برای نقش «مدیر سازمان» انتخابِ سازمان الزامی است');
          setGlobalLoading(false);
          return;
        }
        payload.department_id = Number(departmentId);
        // 9Router (1405/08/11): نامِ مدیر سازمان از همین دیالوگ قابلِ اصلاح است
        payload.name = form.name || '';
      }

      if (form.password) {
        payload.password = form.password;
      }

      // اضافه کردن is_subscribed فقط برای پیمانکارها
      if (form.role === 'Contractor') {
        payload.is_subscribed = form.is_subscribed;
      }

      await api.put(`users/admin/users/${id}/`, payload);
      toast('کاربر با موفقیت ویرایش شد', 'success');
      setOpenEditUser(-1);
      // رفرش صفحه برای نمایش تغییرات
      window.location.reload();
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response?.data?.detail) {
        toast(error.response.data.detail);
      } else {
        toast('خطا در ویرایش کاربر');
      }
    } finally {
      setGlobalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (!form?.id) {
    return (
      <div className={classes.root}>
        <p>کاربر یافت نشد</p>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="نام کاربری"
            variant="outlined"
            value={form.username}
            onChange={(e) => onChange('username', e.target.value)}
            name="edit-user-username"
            autoComplete="off"
            inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
            dir="ltr"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="ایمیل"
            variant="outlined"
            value={form.email}
            onChange={(e) => onChange('email', e.target.value)}
            dir="ltr"
          />
        </Grid>

        {/* 9Router (1405/08/11): نامِ مدیر سازمان — همان که در فهرست کاربران دیده می‌شود */}
        {form.selectedRoles.includes('DepartmentManager') && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="نام مدیر سازمان"
              variant="outlined"
              value={form.name}
              onChange={(e) => onChange('name', e.target.value)}
              name="edit-user-manager-name"
              autoComplete="off"
              inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>نقش‌ها (حداکثر ۲ نقش) *</InputLabel>
            <Select
              multiple
              value={form.selectedRoles}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= 2) {
                  onChange('selectedRoles', val);
                }
              }}
              label="نقش‌ها (حداکثر ۲ نقش) *"
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={roles.find(r => r.value === value)?.label || value}
                      size="small"
                      color={selected.indexOf(value) === 0 ? 'primary' : 'secondary'}
                      onDelete={() => {
                        onChange('selectedRoles', form.selectedRoles.filter(r => r !== value));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </div>
              )}
            >
              {roles.map((r) => (
                <MenuItem
                  key={r.value}
                  value={r.value}
                  disabled={form.selectedRoles.length >= 2 && !form.selectedRoles.includes(r.value)}
                  style={{
                    fontWeight: form.selectedRoles.includes(r.value) ? 'bold' : 'normal',
                    backgroundColor: form.selectedRoles.includes(r.value) ? '#e8eaf6' : 'transparent',
                  }}
                >
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>سازمانِ «مدیر سازمان» *</InputLabel>
            <Select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              label="سازمانِ «مدیر سازمان» *"
              disabled={!form.selectedRoles.includes('DepartmentManager')}
            >
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.display_name || d.name || d.short_name || `#${d.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="موبایل"
            variant="outlined"
            value={form.mobile}
            onChange={(e) => onChange('mobile', e.target.value)}
            placeholder="09123456789"
            name="edit-user-mobile"
            autoComplete="off"
            inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
            dir="ltr"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type={showPwd ? 'text' : 'password'} InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwd((v) => !v)}>{showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>) }}
            label="گذرواژه (اختیاری)"
            variant="outlined"
            value={form.password}
            onChange={(e) => onChange('password', e.target.value)}
            name="edit-user-password"
            autoComplete="new-password"
            inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
            dir="ltr"
          />
          <div style={{ fontSize: 11.5, lineHeight: 2, color: '#475569', marginTop: 6 }}>
            <div style={{ fontWeight: 700, color: '#334155' }}>الزامات رمز (افتا):</div>
            {[
              ['حداقل ۱۰ کاراکتر', form.password.length >= 10],
              ['حرف بزرگ و کوچک', /[a-z]/.test(form.password) && /[A-Z]/.test(form.password)],
              ['حداقل یک رقم', /\d/.test(form.password)],
              ['حداقل یک نویسهٔ ویژه', /[^A-Za-z0-9]/.test(form.password)],
              ['متفاوت با نام کاربری', !!form.password && !form.password.toLowerCase().includes(String(form.username || '').toLowerCase())],
            ].map(([t, ok]) => (
              <div key={t} style={{ color: ok ? '#047857' : '#b91c1c' }}>
                {ok ? '✓' : '•'} {t}
              </div>
            ))}
            <div style={{ color: '#64748b', marginTop: 4 }}>
              رمز نمایش داده نمی‌شود و کاربر در اولین ورود باید آن را عوض کند.
            </div>
          </div>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_active}
                onChange={(e) => onChange('is_active', e.target.checked)}
                color="primary"
              />
            }
            label="فعال"
          />
        </Grid>


      </Grid>

      <div className={classes.buttonContainer}>
        <Button color="primary" variant="contained" onClick={onSave}>
          ذخیره
        </Button>
        <Button variant="outlined" onClick={() => setOpenEditUser(-1)}>
          انصراف
        </Button>
      </div>
    </div>
  );
}

export default EditUser;